import { TrainingRoleplayMicIcon } from "@/components/lead/sections/TrainingRoleplayMicIcon";
import type { TrainingRoleplayPanelVm } from "@/components/lead/sections/useTrainingRoleplayPanel";
import type { RoleplayScenarioListItem, RoleplayScore } from "@/lib/training/trainingApiClient";
import {
  cardSurfaceClass,
  cardTitleTextClass,
  inputClass,
  secondaryBtnClass,
  secondarySurfaceClass,
} from "@/components/common/surfaceClasses";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

function scenarioLine(s: RoleplayScore): string {
  const n = (v: number | undefined) => (typeof v === "number" && !Number.isNaN(v) ? v.toFixed(1) : "—");
  const leg = s.empathy;
  return `Clarity ${n(s.clarity)} · Accuracy ${n(s.accuracy)} · Answers customer ${n(s.answersCustomerQuestions ?? leg)} · Follow-ups ${n(s.usesFollowUpQuestions ?? leg)} · Open-ended ${n(s.usesOpenEndedQuestions ?? leg)} · Concerns ${n(s.objectionHandling ?? leg)}`;
}

function scenarioSourceSuffix(s: RoleplayScenarioListItem): string {
  if (s.source === "manufacturer") return " (manufacturer)";
  if (s.source === "org") return " (org)";
  if (s.source === "platform_example") return " (platform example)";
  return "";
}

export function TrainingRoleplayPanelView(props: { vm: TrainingRoleplayPanelVm }) {
  const vm = props.vm;
  const scenarioOptions = vm.scenarioOptions;
  const activeScenario = scenarioOptions.find((s) => s.key === vm.scenarioKey);
  const hasScenarios = scenarioOptions.length > 0;

  return (
    <div className="w-full text-left space-y-4">
      <div className={`${cardSurfaceClass} p-5`}>
        <h2 className={`text-2xl font-bold ${cardTitleTextClass}`}>Sales roleplay</h2>
        <p className="text-sm text-gray-600 mt-1 dark:text-zinc-400">
          Practice with an AI customer. Grounding uses the knowledge base on each turn unless your org pins a KB digest on
          the scenario and enables “freeze digest” under Manager → Training Authoring (trainer+).
        </p>
      </div>

      {!vm.organizationId ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-200">
          Select a workspace organization to start roleplay.
        </div>
      ) : null}

      {vm.progressErr ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/35 dark:text-red-300">{vm.progressErr}</div>
      ) : null}

      {vm.canReview && vm.progress && vm.organizationId && (vm.progress.pendingHumanReviews ?? 0) > 0 ? (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-950 dark:border-indigo-800 dark:bg-indigo-950/35 dark:text-indigo-100">
          <strong>{vm.progress.pendingHumanReviews}</strong> scored roleplay session
          {(vm.progress.pendingHumanReviews ?? 0) === 1 ? "" : "s"} in this org await human review. Open the{" "}
          <strong>Reviews</strong> tab to grade them.
        </div>
      ) : null}

      {vm.progress && vm.organizationId ? (
        <div className={`${secondarySurfaceClass} p-4 text-sm text-gray-700 dark:text-zinc-300`}>
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-2">Your progress</div>
          <div className="flex flex-wrap gap-4">
            <span>
              <strong>{vm.progress.totalSessions}</strong> sessions logged
            </span>
            {vm.progress.bestOverallScore > 0 ? (
              <span>
                Best score: <strong>{vm.progress.bestOverallScore.toFixed(1)}</strong>/10
              </span>
            ) : null}
            {vm.progress.lastSessionAt ? (
              <span className="text-gray-500 dark:text-zinc-400">Last: {new Date(vm.progress.lastSessionAt).toLocaleString()}</span>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={`${secondarySurfaceClass} p-4 space-y-3`}>
        {vm.phase === "pick" ? (
          <>
            {!hasScenarios && vm.organizationId ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                No roleplay scenarios are configured for this workspace yet. A trainer can add them under{" "}
                <strong>Manager → Training Authoring</strong> (or inherit sharable scenarios from a linked manufacturer).
              </div>
            ) : null}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full max-w-xl">
              <span className="text-sm font-semibold text-gray-700 dark:text-zinc-300 shrink-0">Scenario:</span>
              <select
                {...{ [FIREFLY_TARGET_ATTR]: "training-scenario-list" }}
                className={`w-full sm:flex-1 min-w-0 ${inputClass} rounded-xl disabled:opacity-60`}
                value={vm.scenarioKey}
                onChange={(e) => vm.setScenarioKey(e.target.value)}
                aria-label="Training scenario"
                disabled={!hasScenarios}
              >
                {!hasScenarios ? (
                  <option value="">—</option>
                ) : (
                  scenarioOptions.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                      {scenarioSourceSuffix(s)}
                    </option>
                  ))
                )}
              </select>
            </div>
            <p className="text-xs text-gray-600 dark:text-zinc-400">{activeScenario?.persona}</p>
            <button
              type="button"
              {...{ [FIREFLY_TARGET_ATTR]: "training-start-roleplay" }}
              disabled={!vm.organizationId || vm.loading || !vm.scenarioKey.trim()}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => void vm.startSession()}
            >
              {vm.loading ? "Starting…" : "Start roleplay"}
            </button>
          </>
        ) : null}

        {vm.phase === "live" || vm.phase === "scored" ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 items-center justify-between">
              <span className="text-xs font-semibold text-gray-600 dark:text-zinc-400">
                {activeScenario?.label}
              </span>
              <button
                type="button"
                className="text-xs text-gray-600 underline dark:text-zinc-400"
                onClick={vm.reset}
                disabled={vm.loading}
              >
                New session
              </button>
            </div>

            <div className={`max-h-72 overflow-y-auto space-y-2 ${cardSurfaceClass} p-3`}>
              {vm.messages.map((m, i) => (
                <div
                  key={`${i}-${m.role}`}
                  className={`text-sm rounded-lg px-3 py-2 ${
                    m.role === "customer"
                      ? "bg-slate-100 text-gray-900 dark:bg-zinc-800 dark:text-zinc-100"
                      : "bg-indigo-50 text-indigo-950 ml-4 dark:bg-indigo-950/40 dark:text-indigo-100"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-0.5">
                    {m.role === "customer" ? "Customer" : "You"}
                  </div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
            </div>

            {vm.phase === "live" ? (
              <>
                <div className={`${cardSurfaceClass} p-3 mb-2 space-y-2`}>
                  <div className="flex items-center justify-between">
                    <div className={`text-sm font-semibold inline-flex items-center gap-2 ${cardTitleTextClass}`}>
                      <TrainingRoleplayMicIcon className="w-4 h-4 text-gray-600 dark:text-zinc-400" />
                      Spoken reply
                    </div>
                    {!vm.dictation.isSupported ? (
                      <div className="text-xs text-gray-500 dark:text-zinc-400">Not supported in this browser</div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${
                        vm.dictation.isSupported && !vm.dictation.isListening
                          ? "bg-black text-white dark:bg-zinc-100 dark:text-zinc-900"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-700 dark:text-zinc-400"
                      }`}
                      disabled={!vm.dictation.isSupported || vm.dictation.isListening || vm.loading}
                      onClick={() => {
                        vm.setError(null);
                        vm.dictation.start();
                      }}
                      aria-label="Start dictation"
                    >
                      <TrainingRoleplayMicIcon className="w-4 h-4" />
                      Dictate
                    </button>
                    <button
                      type="button"
                      className={`rounded-xl px-3 py-2 text-sm font-semibold ${
                        vm.dictation.isListening
                          ? "bg-black text-white dark:bg-zinc-100 dark:text-zinc-900"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-700 dark:text-zinc-400"
                      }`}
                      disabled={!vm.dictation.isListening}
                      onClick={vm.dictation.stop}
                    >
                      Stop
                    </button>
                    <button
                      type="button"
                      className={`rounded-xl px-3 py-2 text-sm font-semibold ${
                        vm.dictation.draft.trim()
                          ? `${secondaryBtnClass}`
                          : "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-700 dark:text-zinc-400"
                      }`}
                      disabled={!vm.dictation.draft.trim() || vm.loading}
                      onClick={vm.confirmDictationReply}
                    >
                      Confirm → Send
                    </button>
                  </div>
                  {vm.dictation.error ? (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-2 dark:bg-red-950/40 dark:border-red-800 dark:text-red-300">
                      Dictation error: {vm.dictation.error}
                    </div>
                  ) : null}
                  {vm.dictation.draft.trim() ? (
                    <div className={`text-sm ${secondarySurfaceClass} p-2`}>
                      <div className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Heard</div>
                      <div className={`whitespace-pre-wrap ${cardTitleTextClass}`}>{vm.dictation.draft}</div>
                      <div className="text-xs text-gray-500 dark:text-zinc-400 mt-2">
                        Confirm to send as your reply, or edit below first.
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    className={`flex-1 ${inputClass} rounded-xl text-sm`}
                    placeholder="Your reply as salesperson…"
                    value={vm.input}
                    onChange={(e) => vm.setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void vm.sendSalesLine();
                      }
                    }}
                  />
                  <button
                    type="button"
                    disabled={!vm.input.trim() || vm.loading}
                    className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    onClick={() => void vm.sendSalesLine()}
                  >
                    Send
                  </button>
                  <button
                    type="button"
                    disabled={vm.loading || vm.messages.length < 2}
                    className={`rounded-xl ${secondaryBtnClass} px-4 py-2 text-sm font-semibold disabled:opacity-50`}
                    onClick={() => void vm.endAndScore()}
                  >
                    End &amp; score
                  </button>
                </div>
              </>
            ) : null}

            {vm.phase === "scored" && vm.score ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-2 dark:border-emerald-800 dark:bg-emerald-950/30">
                <div className="text-sm font-bold text-emerald-900 dark:text-emerald-200">
                  Session score: {vm.score.overallScore.toFixed(1)} / 10
                </div>
                <div className="text-xs text-gray-600 flex flex-wrap gap-2">{scenarioLine(vm.score)}</div>
                <p className="text-sm text-gray-800">{vm.score.summary}</p>
                <div className="grid sm:grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="font-semibold text-gray-700 mb-1">Strengths</div>
                    <ul className="list-disc pl-4 space-y-0.5 text-gray-600">
                      {(vm.score.strengths ?? []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-700 mb-1">Improvements</div>
                    <ul className="list-disc pl-4 space-y-0.5 text-gray-600">
                      {(vm.score.improvements ?? []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {vm.error ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 dark:bg-red-950/40 dark:border-red-800 dark:text-red-300">{vm.error}</div>
        ) : null}
      </div>
    </div>
  );
}
